import { PencilIcon } from "../../../automationStudioIcons";
import { InputBox } from "../../../../FormComponent";
import SmartInputBox from "../../shared/SmartInputBox";
import StepExecutionStatusBadge from "../../shared/StepExecutionStatusBadge";
import styles from "../../../../../styles/automationStudio/automationStudio.module.scss";
import { LABELS } from "../../../content";
import { EVALUATION_TOOL } from "../../../../AutomationStudio/constant";
import { formatDuration } from "../../../../AutomationStudio/utils";
import { getReplayStepToolForDisplay } from "../../../../AutomationStudio/utils/stepExecutionDisplay";
import { convertIdsToDisplay, replaceFileRefsWithNames } from "../../../../../utils/automation/variableUtils";

export default function StepEditorDescriptionSection({
    localStep,
    displayStep,
    isEditorLocked,
    isEditingDescription,
    setIsEditingDescription,
    isHoveringDescription,
    setIsHoveringDescription,
    handleLocalStepChange,
    variablesList,
    variablesListDescriptions,
    descriptionFileSuggestions,
    fileDescriptions,
    isVerificationStep,
    isEvaluationTool,
    isReplayFlow,
    showDynamicStepBadge,
    replayDetail,
    detailForUi,
    instructions,
    statusOptions,
    verificationUiState,
    shouldMaskRecordingOutcome,
    isDescriptionDirty = false,
}) {
    const hasTerminalExecutionStatus =
        displayStep.status === 'success' ||
        displayStep.status === 'failed' ||
        displayStep.status === 'completed' ||
        displayStep.status === 'failed_by_evaluation';
    const toolDisplay = (() => {
        const replayTool = getReplayStepToolForDisplay(replayDetail, displayStep);
        if (replayTool) return replayTool;
        if (displayStep.tool) return displayStep.tool;
        return isVerificationStep ? EVALUATION_TOOL : '—';
    })();

    return (
        <div className={styles.stepDescriptionSection}>
            <div
                className={styles.stepDescriptionEditWrap}
                onMouseEnter={() => setIsHoveringDescription(true)}
                onMouseLeave={() => setIsHoveringDescription(false)}
                onClick={() => { if (!isEditorLocked && !isEditingDescription) setIsEditingDescription(true); }}
                style={{ cursor: isEditorLocked || isEditingDescription ? "default" : "pointer" }}
            >
                {isEditingDescription ? (
                    <SmartInputBox
                        size="small"
                        value={localStep.description || ''}
                        onChange={(e) => {
                            const val = e.target.value;
                            handleLocalStepChange({
                                description: val,
                                stepDescription: val,
                                extractedData: { ...localStep.extractedData, stepDescription: val },
                                ...(localStep.args?.stepDescription !== undefined && {
                                    args: { ...localStep.args, stepDescription: val }
                                })
                            });
                        }}
                        onBlur={() => setIsEditingDescription(false)}
                        placeholder={LABELS.STEP_DESCRIPTION_PLACEHOLDER}
                        autoFocus
                        disabled={isEditorLocked}
                        suggestions={variablesList}
                        descriptions={variablesListDescriptions}
                        fileTrigger="@"
                        fileSuggestions={descriptionFileSuggestions}
                        fileDescriptions={fileDescriptions}
                        formatFileInsertion={(file) => `@[${file.id}]`}
                        customClassName={isDescriptionDirty ? styles.dirtyField : ''}
                    />
                ) : (
                    <InputBox
                        size="small"
                        value={replaceFileRefsWithNames(
                            convertIdsToDisplay(
                                localStep.description || '',
                                variablesList,
                                variablesListDescriptions
                            ),
                            descriptionFileSuggestions
                        ) || ''}
                        placeholder="Step description"
                        readOnly
                        endIcon={isHoveringDescription && !isEditorLocked
                            ? <PencilIcon style={{ width: '12px', height: '12px' }} />
                            : null
                        }
                        onEndIconClick={() => setIsEditingDescription(true)}
                        customClassName={isDescriptionDirty ? styles.dirtyField : ''}
                    />
                )}
            </div>
            <div className={styles.stepMetadataGrid}>
                <StepExecutionStatusBadge
                    step={displayStep}
                    execDetail={detailForUi}
                    instructions={instructions}
                    statusOptions={statusOptions}
                />
                {showDynamicStepBadge ? (
                    <span
                        className={styles.quickStatsDynamicChip}
                        role="status"
                        aria-label={LABELS.STEP_DYNAMIC_BADGE}
                        title={LABELS.STEP_DYNAMIC_BADGE}
                    >
                        {LABELS.STEP_DYNAMIC_BADGE}
                    </span>
                ) : null}
                <div className={styles.metadataCard}>
                    <span className={styles.metadataLabel}>
                        {isVerificationStep ? 'Type:' : 'Tool:'}
                    </span>
                    <span className={styles.metadataValue} title={toolDisplay}>
                        {toolDisplay}
                    </span>
                </div>
                {displayStep.duration && !shouldMaskRecordingOutcome && (
                    <div className={styles.metadataCard}>
                        <span className={styles.metadataLabel}>Duration:</span>
                        <span className={styles.metadataValue}>{formatDuration(displayStep.duration)}</span>
                    </div>
                )}
                {(() => {
                    if (shouldMaskRecordingOutcome) return null;
                    if (!hasTerminalExecutionStatus) return null;
                    const tsRaw = displayStep.executedAt ?? replayDetail?.result?.timestamp ?? displayStep.timestamp;
                    if (!tsRaw) return null;
                    const d = new Date(tsRaw);
                    if (Number.isNaN(d.getTime())) return null;
                    const formatted = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                    return (
                        <div className={styles.metadataCard}>
                            <span className={styles.metadataLabel}>Executed at:</span>
                            <span className={styles.metadataValue}>{formatted}</span>
                        </div>
                    );
                })()}
                {isEvaluationTool && displayStep.confidence != null && !isReplayFlow && verificationUiState.isPassed && (
                    <div className={styles.metadataCard}>
                        <span className={styles.metadataLabel}>Confidence:</span>
                        <span className={styles.metadataValue}>{displayStep.confidence}%</span>
                    </div>
                )}
            </div>
        </div>
    );
}
